import { useState, useEffect } from 'react';
import { api } from '../api/client.js';
import { FEELINGS, FEELING_REASONS, FATIGUE_OPTIONS, CLOSURE_OPTIONS, BOSS_PERCEPTION, TEAM_FEELING, DOMINANT_PHRASE, QUIT_THOUGHT, DECRETOS_CALIBRACION, CIERRE_COSECHA } from '../utils/constants.js';
import { GlossaryText } from '../components/GlossaryTerm.jsx';
import BiometricScanner from '../components/BiometricScanner.jsx';
import { analyzeLinguisticPattern } from '../utils/nlp.js';
import './Survey.css';

export default function Survey() {
    const [step, setStep] = useState('loading');
    const [todaySurvey, setTodaySurvey] = useState(null);
    const [weeklyDone, setWeeklyDone] = useState(false);
    const [selected, setSelected] = useState({});
    const [biometricData, setBiometricData] = useState(null);
    const [nlpText, setNlpText] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [toast, setToast] = useState('');

    const [showCalibration, setShowCalibration] = useState(false);
    const [harvestMessage, setHarvestMessage] = useState('');

    useEffect(() => { 
        loadState(); 
        setHarvestMessage(CIERRE_COSECHA[Math.floor(Math.random() * CIERRE_COSECHA.length)]);
    }, []);

    async function loadState() {
        try {
            const [today, weekly] = await Promise.all([api.getTodaySurvey(), api.getCurrentWeekly()]);
            setTodaySurvey(today);
            setWeeklyDone(!!weekly);

            if (!today?.start) setStep('camera_start');
            else if (!today?.end) setStep('end');
            else if (!weekly && new Date().getDay() === 5) setStep('weekly');
            else setStep('done');
        } catch {
            setStep('camera_start');
        }
    }

    const handleBiometricComplete = (data) => {
        setBiometricData(data);
        setStep('start');
    };

    function skipCamera() {
        setBiometricData(null);
        setStep('start');
    }

    async function handleSubmitStart() {
        if ((selected.feeling === 'regular' || selected.feeling === 'mal' || biometricData?.feeling === 'mal') && !showCalibration) {
            setShowCalibration(true);
            return;
        }

        setSubmitting(true);
        // Determine if there is inconsistency
        const isInconsistent = selected.feeling === 'bien' && biometricData?.feeling === 'mal';

        try {
            await api.submitDailySurvey({
                surveyType: 'start',
                feeling: selected.feeling,
                feelingReason: selected.feelingReason,
                blinkRate: biometricData?.bpm,
                eyelidOpening: biometricData?.ear,
                somaticTension: biometricData?.tension,
                biometricFeeling: biometricData?.feeling,
                isInconsistent: isInconsistent ? 1 : 0
            });
            const refreshed = await api.getTodaySurvey();
            setTodaySurvey(refreshed);
            showToast(isInconsistent ? 'Presencia detectada con tensión. Calibrando...' : '¡Presencia calibrada!');
            setStep('done_start');
            setTimeout(() => setStep(refreshed?.end ? 'done' : 'end_preview'), 3000);
        } catch (err) {
            showToast(err.message);
        } finally {
            setSubmitting(false);
        }
    }

    async function handleSubmitEnd() {
        setSubmitting(true);
        try {
            await api.submitDailySurvey({
                surveyType: 'end',
                fatigue: selected.fatigue,
                closure: selected.closure,
            });
            showToast('¡Gracias! Buen cierre de jornada.');
            const isFriday = new Date().getDay() === 5;
            setStep(!weeklyDone && isFriday ? 'weekly' : 'done');
        } catch (err) {
            showToast(err.message);
        } finally {
            setSubmitting(false);
        }
    }

    async function handleSubmitWeekly() {
        setSubmitting(true);
        // Analyze NLP Text
        const nlpResult = analyzeLinguisticPattern(nlpText);
        
        try {
            await api.submitWeeklySurvey({
                ...selected,
                rawInputText: nlpText,
                nlpConnectors: JSON.stringify({
                    y: nlpResult.y,
                    pero: nlpResult.pero,
                    por_que: nlpResult.por_que
                })
            });
            showToast('¡Gracias por tu reflexión semanal!');
            setStep('done');
        } catch (err) {
            showToast(err.message);
        } finally {
            setSubmitting(false);
        }
    }

    function showToast(msg) {
        setToast(msg);
        setTimeout(() => setToast(''), 3000);
    }

    function renderOption(key, value, options) {
        return (
            <button
                key={value}
                className={`btn-survey ${selected[key] === value ? 'selected' : ''}`}
                onClick={() => setSelected(s => ({ ...s, [key]: value }))}
            >
                <span className="emoji">{options[value].emoji}</span>
                {options[value].label}
            </button>
        );
    }

    const startPending = (() => {
        if (step !== 'start') return { count: 0, messages: [] };
        const msgs = [];
        if (!selected.feeling) msgs.push('Selecciona tu nivel de energía.');
        const needsReason =
            selected.feeling === 'regular' ||
            selected.feeling === 'mal' ||
            (biometricData?.feeling === 'mal' && selected.feeling);
        if (needsReason && !selected.feelingReason) msgs.push('Selecciona qué está ocupando tu espacio hoy.');
        return { count: msgs.length, messages: msgs };
    })();

    const endPending = (() => {
        if (step !== 'end') return { count: 0, messages: [] };
        const msgs = [];
        if (!selected.fatigue) msgs.push('Selecciona tu nivel de cansancio.');
        if (selected.fatigue && !selected.closure) msgs.push('Completa el gesto de balance final.');
        return { count: msgs.length, messages: msgs };
    })();

    if (step === 'loading') {
        return <div className="loading-screen"><div className="spinner" /></div>;
    }

    if (step === 'done' || step === 'end_preview') {
        return (
            <div className="container animate-in survey-done">
                <div className="survey-done-icon">🧘</div>
                <h2>¡Presencia Calibrada!</h2>
                <div style={{ padding: '1.5rem', background: '#f8fafc', borderRadius: '16px', border: '1px dashed #cbd5e1', marginBottom: '2rem' }}>
                    <p style={{ fontStyle: 'italic', color: '#64748b', fontSize: '1.1rem', lineHeight: '1.6' }}>"{harvestMessage}"</p>
                </div>
                <p className="text-muted">Tu sintonía de hoy ha sido registrada en la manada.</p>
                {step === 'end_preview' && (
                    <button className="btn btn-primary mt-lg" onClick={() => { setSelected({}); setStep('end'); }}>
                        Gesto de Cierre de Jornada
                    </button>
                )}
            </div>
        );
    }

    return (
        <div className="container animate-in">
            {/* START OF SHIFT SURVEY */}
            {step === 'camera_start' && (
                <div className="survey-section stagger" style={{ textAlign: 'center' }}>
                    <h2 className="survey-title">Espejo de calibración 🪞</h2>
                    <p className="text-muted mb-lg">Si puedes, centra tu rostro unos segundos; si no, continúa sin cámara.</p>
                    <div className="mb-xl">
                        <BiometricScanner metricsOnly onDataCaptured={handleBiometricComplete} />
                    </div>
                    <button type="button" className="btn btn-secondary btn-lg w-full" onClick={skipCamera}>
                        Continuar sin cámara
                    </button>
                </div>
            )}

            {step === 'start' && (
                <div className="survey-section stagger">
                    {!showCalibration ? (
                        <>
                            <h2 className="survey-title">¿Cómo está tu presencia hoy? 🧭</h2>
                            
                            <div className="survey-options">
                                {Object.entries(FEELINGS).map(([val]) => renderOption('feeling', val, FEELINGS))}
                            </div>

                            {(selected.feeling === 'regular' || selected.feeling === 'mal' || (biometricData?.feeling === 'mal' && selected.feeling)) && (
                                <div className="survey-followup animate-in">
                                    <h3 className="survey-subtitle">¿Sientes algo de esto ocupando tu espacio?</h3>
                                    <div className="survey-options">
                                        {Object.entries(FEELING_REASONS).map(([val]) => renderOption('feelingReason', val, FEELING_REASONS))}
                                    </div>
                                </div>
                            )}

                            <button
                                className="btn btn-primary btn-lg w-full mt-lg animate-fade"
                                onClick={handleSubmitStart}
                                disabled={submitting || startPending.count > 0}
                            >
                                {submitting ? <span className="spinner" /> : 'Sincronizar mi día'}
                            </button>
                            {startPending.count > 0 && (
                                <div className="text-sm text-muted mt-sm">
                                    <strong>Faltan {startPending.count} respuesta(s):</strong> {startPending.messages[0]}
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="animate-in" style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⚡</div>
                            <h2 style={{ marginBottom: '1.5rem', color: 'var(--color-primary)' }}>Decreto de Calibración</h2>
                            <div style={{ padding: '1.5rem', background: 'white', borderRadius: '20px', boxShadow: '0 10px 25px rgba(0,0,0,0.05)', marginBottom: '2rem' }}>
                                <p style={{ fontSize: '1.1rem', lineHeight: '1.7', color: '#334155' }}>
                                    <GlossaryText>
                                        {DECRETOS_CALIBRACION[selected.feelingReason] ||
                                            'Tu biometría detecta una carga alostática. Respira, suelta los hombros y enfócate solo en el próximo gesto técnico.'}
                                    </GlossaryText>
                                </p>
                            </div>
                            <button className="btn btn-primary btn-lg w-full shadow-lg" onClick={handleSubmitStart} disabled={submitting}>
                                {submitting ? <span className="spinner" /> : 'Entendido, labrar con consciencia'}
                            </button>
                        </div>
                    )}
                </div>
            )}

            {step === 'done_start' && (
                <div className="survey-done animate-in">
                    <div className="survey-done-icon">✨</div>
                    <h2>¡Sintonizado!</h2>
                    <p>Labra tu día con excelencia.</p>
                </div>
            )}

            {/* END OF SHIFT SURVEY */}
            {step === 'end' && (
                <div className="survey-section stagger">
                    <h2 className="survey-title">Al cierre del labrar, ¿cómo está tu energía? ⚡</h2>
                    <div className="survey-options">
                        {Object.entries(FATIGUE_OPTIONS).map(([val]) => renderOption('fatigue', val, FATIGUE_OPTIONS))}
                    </div>

                    {selected.fatigue && (
                        <div className="survey-followup animate-in">
                            <h3 className="survey-subtitle">Gesto de balance final</h3>
                            <div className="survey-options">
                                {Object.entries(CLOSURE_OPTIONS).map(([val]) => renderOption('closure', val, CLOSURE_OPTIONS))}
                            </div>
                        </div>
                    )}

                    <button
                        className="btn btn-primary btn-lg w-full mt-lg animate-fade"
                        onClick={handleSubmitEnd}
                        disabled={submitting || endPending.count > 0}
                    >
                        {submitting ? <span className="spinner" /> : 'Cerrar mi presencia'}
                    </button>
                    {endPending.count > 0 && (
                        <div className="text-sm text-muted mt-sm">
                            <strong>Faltan {endPending.count} respuesta(s):</strong> {endPending.messages[0]}
                        </div>
                    )}
                </div>
            )}

            {/* WEEKLY SURVEY */}
            {step === 'weekly' && (
                <div className="survey-section stagger">
                    <h2 className="survey-title">Gesto de Sintonía Semanal 📅</h2>

                    <h3 className="survey-subtitle">Resonancia con el guía de la manada...</h3>
                    <div className="survey-options">
                        {Object.entries(BOSS_PERCEPTION).map(([val]) => renderOption('bossPerception', val, BOSS_PERCEPTION))}
                    </div>

                    {selected.bossPerception && (
                        <div className="animate-in">
                            <h3 className="survey-subtitle mt-lg">Sintonía con tus pares...</h3>
                            <div className="survey-options">
                                {Object.entries(TEAM_FEELING).map(([val]) => renderOption('teamFeeling', val, TEAM_FEELING))}
                            </div>
                        </div>
                    )}

                    {selected.teamFeeling && (
                        <div className="animate-in">
                            <h3 className="survey-subtitle mt-lg">Eco de tu pensamiento dominante...</h3>
                            <div className="survey-options">
                                {Object.entries(DOMINANT_PHRASE).map(([val]) => renderOption('dominantPhrase', val, DOMINANT_PHRASE))}
                            </div>
                        </div>
                    )}

                    {selected.dominantPhrase && (
                        <div className="animate-in">
                            <h3 className="survey-subtitle mt-lg">Observación Ontológica (Escrito)</h3>
                            <p className="text-muted mb-sm">Describe brevemente cómo visualizas tu próxima semana...</p>
                            <textarea 
                                className="survey-textarea"
                                value={nlpText}
                                onChange={(e) => setNlpText(e.target.value)}
                                placeholder="Escribe aquí tu pensamiento labrado..."
                                rows={4}
                            />
                        </div>
                    )}

                    {selected.dominantPhrase && (
                        <div className="animate-in mt-lg">
                            <h3 className="survey-subtitle mt-lg">¿Sientes la fuerza para seguir labrando?</h3>
                            <div className="survey-options">
                                {Object.entries(QUIT_THOUGHT).map(([val]) => renderOption('thoughtOfQuitting', val, QUIT_THOUGHT))}
                            </div>
                        </div>
                    )}

                    {selected.bossPerception && selected.teamFeeling && selected.dominantPhrase && selected.thoughtOfQuitting && (
                        <button className="btn btn-primary btn-lg w-full mt-lg animate-fade" onClick={handleSubmitWeekly} disabled={submitting}>
                            {submitting ? <span className="spinner" /> : 'Sellar mi semana'}
                        </button>
                    )}
                </div>
            )}

            {toast && <div className="toast success">{toast}</div>}
        </div>
    );
}
